function marameStyleRailState(left, scrollWidth, clientWidth, count) {
  const maximum = Math.max(0, scrollWidth - clientWidth);
  const position = Math.max(0, Math.min(maximum, left));
  return { maximum, position, active: maximum ? Math.min(count - 1, Math.round(position / maximum * (count - 1))) : 0, previous: position > 1, next: position < maximum - 1 };
}
function marameStyleStep(left, maximum, offsets, direction) {
  const points = [...new Set([0, ...offsets.map(value => Math.max(0, Math.min(maximum, value))), maximum])].sort((a,b)=>a-b);
  return direction > 0 ? points.find(value=>value>left+2) ?? maximum : points.reverse().find(value=>value<left-2) ?? 0;
}
function marameStyleRequestUrl(href, sectionId, variantId) {
  if (!/^\d+$/.test(String(variantId)) || !sectionId) throw new Error('Invalid style request');
  const url = new URL(href);
  if (!/\/products\/[^/]+\/?$/.test(url.pathname)) throw new Error('Invalid product path');
  url.searchParams.set('variant', String(variantId));
  url.searchParams.set('section_id', sectionId);
  url.searchParams.delete('option_values');
  return url;
}
if (!customElements.get('marame-cross-sells')) {
  customElements.define('marame-cross-sells', class extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.initRail();
      this.notifyStyleProducts();
      if (this.hasAttribute('data-marame-immersive') && typeof IntersectionObserver !== 'undefined') {
        if (!matchMedia('(prefers-reduced-motion: reduce)').matches) this.setAttribute('data-motion-ready', '');
        this.motionObserver = new IntersectionObserver(([entry]) => this.setImmersive(entry.isIntersecting && entry.intersectionRatio >= .08), { threshold: .08 });
        this.motionObserver.observe(this);
      }
      this.selectionUnsubscribe = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
        const main = document.querySelector('product-info[id^="MainProduct"]');
        if (!main || data.target?.closest('product-info') !== main) return;
        this.controller?.abort();
        this.querySelector('[data-cross-sell-content]').hidden = true;
        this.setImmersive(false);
        this.notifyStyleProducts();
      });
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const main = document.querySelector('product-info[id^="MainProduct"]');
        if (!main || main.dataset.section !== data.sectionId || !data.variant?.id) return;
        if (this.dataset.variantId === String(data.variant.id)) {
          this.querySelector('[data-cross-sell-content]').hidden = false;
          this.syncImmersive();
          this.notifyStyleProducts();
          return;
        }
        this.refresh(data.variant.id);
      });
    }
    disconnectedCallback() {
      this.setImmersive(false);
      this.connected = false;
      this.unsubscribe?.(); this.selectionUnsubscribe?.(); this.controller?.abort();
      this.railEvents?.abort(); this.resizeObserver?.disconnect(); this.motionObserver?.disconnect();
      if (this.railFrame) cancelAnimationFrame(this.railFrame);
      this.notifyStyleProducts();
    }
    notifyStyleProducts() {
      document.dispatchEvent(new CustomEvent('marame:style-products-change', { detail: { productId: this.dataset.productId, sectionId: this.dataset.sectionId } }));
    }
    setImmersive(active) {
      active=Boolean(this.hasAttribute('data-marame-immersive') && active && this.querySelector('[data-style-strip]') && !this.querySelector('[data-cross-sell-content]').hidden);
      if (this.dataset.immersiveActive===String(active)) return;
      this.dataset.immersiveActive=String(active);
      this.classList.toggle('is-immersive',active);
      const event=new CustomEvent('marame:immersive-change',{bubbles:true,detail:{sectionId:this.dataset.sectionId,active}});
      if(this.isConnected)this.dispatchEvent(event);else document.dispatchEvent(event);
    }
    syncImmersive() {
      const rect=this.getBoundingClientRect();
      const visible=Math.max(0,Math.min(rect.bottom,window.innerHeight)-Math.max(rect.top,0));
      this.setImmersive(rect.height>0 && visible/rect.height>=.08);
    }
    initRail() {
      this.railEvents?.abort(); this.resizeObserver?.disconnect();
      if (this.railFrame) cancelAnimationFrame(this.railFrame);
      this.railFrame = null;
      this.strip = this.querySelector('[data-style-strip]');
      if (!this.strip) return;
      this.railEvents = new AbortController();
      const options = { signal:this.railEvents.signal };
      this.querySelector('[data-style-previous]').addEventListener('click',()=>this.step(-1),options);
      this.querySelector('[data-style-next]').addEventListener('click',()=>this.step(1),options);
      this.strip.addEventListener('keydown',event=>{
        if (event.target !== this.strip || event.altKey || event.metaKey || event.ctrlKey) return;
        if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') this.scrollTo(event.key === 'Home' ? 0 : this.strip.scrollWidth-this.strip.clientWidth);
        else this.step(event.key === 'ArrowRight' ? 1 : -1);
      },options);
      this.strip.addEventListener('scroll',()=>{
        if (this.railFrame) return;
        this.railFrame=requestAnimationFrame(()=>{this.railFrame=null;this.updateRail();});
      },{...options,passive:true});
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver=new ResizeObserver(()=>this.updateRail());
        this.resizeObserver.observe(this.strip);
      }
      this.updateRail();
    }
    updateRail() {
      if (!this.strip) return;
      const count=this.strip.children.length;
      const state=marameStyleRailState(this.strip.scrollLeft,this.strip.scrollWidth,this.strip.clientWidth,count);
      this.querySelector('[data-style-controls]').hidden=state.maximum<2;
      this.querySelector('[data-style-previous]').disabled=!state.previous;
      this.querySelector('[data-style-next]').disabled=!state.next;
      const dots=this.querySelector('[data-style-dots]');dots.hidden=count<2 || state.maximum<2;
      [...dots.children].forEach((dot,i)=>dot.toggleAttribute('data-current',i===state.active));
      const status=this.querySelector('[data-style-status]');
      status.textContent=status.dataset.label.replace('[index]',String(state.active+1)).replace('[count]',String(count));
    }
    step(direction) {
      const maximum=Math.max(0,this.strip.scrollWidth-this.strip.clientWidth);
      this.scrollTo(marameStyleStep(this.strip.scrollLeft,maximum,[...this.strip.children].map(card=>card.offsetLeft-parseFloat(getComputedStyle(this.strip).paddingLeft || '0')),direction));
    }
    scrollTo(left) {
      this.strip.scrollTo({left,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
    }
    async refresh(variantId) {
      this.controller?.abort();
      const controller=new AbortController();this.controller=controller;
      const content=this.querySelector('[data-cross-sell-content]');content.hidden=true;this.setImmersive(false);this.setAttribute('aria-busy','true');
      this.notifyStyleProducts();
      try {
        const url=marameStyleRequestUrl(window.location.href,this.dataset.sectionId,variantId);
        const response=await fetch(url,{signal:controller.signal,credentials:'same-origin'});
        if (!response.ok) throw new Error('Unable to refresh styling');
        const text=await response.text();
        if (new TextEncoder().encode(text).byteLength>300000) throw new Error('Styling response budget');
        const html=new DOMParser().parseFromString(text,'text/html');
        const section=html.querySelector('marame-cross-sells');
        const updated=section?.querySelector('[data-cross-sell-content]');
        if (controller.signal.aborted || this.controller!==controller || !this.isConnected) return;
        if (!updated || section.dataset.productId!==this.dataset.productId || section.dataset.variantId!==String(variantId) || section.dataset.sectionId!==this.dataset.sectionId) throw new Error('Styling response identity');
        content.replaceChildren(...updated.childNodes);this.dataset.variantId=String(variantId);content.hidden=false;this.initRail();this.syncImmersive();
        this.notifyStyleProducts();
      } catch(error) {
        if (this.controller===controller && error.name!=='AbortError') content.hidden=true;
      } finally {
        if (this.controller===controller) this.removeAttribute('aria-busy');
      }
    }
  });
}
